import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Tabs, Tab, Paper, CircularProgress } from '@mui/material';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import PageHeader from '../../ui-components/PageHeader';
import ContentTable from './ContentTable';
import ContentBoard from './ContentBoard';
import MarketingTasks from './MarketingTasks';
import MarketingDashboard from './MarketingDashboard';
import MarketingMeetings from './MarketingMeetings';
import ContentCalendar from './ContentCalendar';

const TAB_KEYS = ['content', 'taken', 'meetings', 'dashboard'];

export default function MarketingOS() {
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('board'); // 'board' or 'table'

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, tasksRes, eventsRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/marketing/content-items?limit=300`),
        authFetch(`${BACKEND_URL}/Api/marketing/tasks?limit=300`),
        authFetch(`${BACKEND_URL}/Api/marketing/events?limit=100`),
      ]);
      if (itemsRes.ok) setItems(await itemsRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
    } catch (err) {
      console.error('[MarketingOS] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Persist tab in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (t && TAB_KEYS.includes(t)) setTab(TAB_KEYS.indexOf(t));
  }, []);

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', TAB_KEYS[newTab]);
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
        <PageHeader icon="campaign" title="Marketing OS" subtitle="Content planning, taken en dashboard" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="campaign" title="Marketing OS" subtitle="Content planning, taken en dashboard" />

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label={`Content (${items.filter(i => i.status !== 'ARCHIVED').length})`} />
          <Tab label={`Taken (${tasks.filter(t => t.status !== 'DONE' && t.status !== 'NOT_EXECUTED').length})`} />
          <Tab label="Meetings" />
          <Tab label="Dashboard" />
        </Tabs>
      </Paper>

      {/* Fixed-width tab content area — prevents layout shifts between tabs */}
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        {tab === 0 && (
          <Box>
            {/* View toggle */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {['board', 'tabel', 'kalender'].map(v => (
                <Tab key={v} label={v === 'board' ? 'Board' : v === 'tabel' ? 'Tabel' : 'Kalender'}
                  onClick={() => setView(v)}
                  sx={{ minHeight: 36, py: 0, textTransform: 'none', fontWeight: view === v ? 700 : 400,
                        borderBottom: view === v ? '2px solid #6366f1' : 'none' }} />
              ))}
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              {view === 'board' && <ContentBoard items={items} events={events} onRefresh={fetchAll} />}
              {view === 'tabel' && <ContentTable items={items} events={events} onRefresh={fetchAll} />}
              {view === 'kalender' && <ContentCalendar items={items} events={events} />}
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <MarketingTasks tasks={tasks} items={items} onRefresh={fetchAll} />
        )}

        {tab === 2 && (
          <MarketingMeetings onRefresh={fetchAll} />
        )}

        {tab === 3 && (
          <MarketingDashboard />
        )}
      </Box>
    </Box>
  );
}
